import { useEffect } from 'react';
import { graphql } from 'react-relay';

import { useSubscriptionInterop } from '@confluence/apollo-relay-interop/entry-points/useSubscriptionInterop.ts';
import { useSubscriptionValues } from '@confluence/graphql/entry-points/useSubscriptionValues.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getStatusCode } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import type {
	useSuggestedEditsUpdatedSubscription$data,
	useSuggestedEditsUpdatedSubscription$variables,
} from './__generated__/useSuggestedEditsUpdatedSubscription.graphql.ts';

const errorStringsToIgnore = [
	'401',
	'Failed to fetch',
	'Load Failed',
	'Network error',
	'GraphQL error: undefined',
];

type UseSuggestedEditsUpdatedSubscriptionProps = {
	contentId: string;
	contentType: 'page' | 'blogpost';
	onSuggestedEditsUpdated: () => void;
	skip?: boolean;
};

/**
 * Subscribes to the AGG `confluence_onSuggestedEditsUpdated` subscription and
 * invokes `onSuggestedEditsUpdated` whenever an event is received.
 *
 * Mirrors the wiring used for `confluence_onContentModified` (see
 * `@confluence/graphql/SubscriptionsContextProvider` and
 * `ContentTypesTitleSubscription`): uses `useSubscriptionInterop` for
 * cross-build (Apollo / Relay) compatibility, `useSubscriptionValues` for the
 * content ARI and replay extensions, and the standard error reporting path.
 */
export const useSuggestedEditsUpdatedSubscription = ({
	contentId,
	contentType,
	onSuggestedEditsUpdated,
	skip = false,
}: UseSuggestedEditsUpdatedSubscriptionProps): void => {
	const { id, onSubscriptionComplete, extensions, onSubscriptionData } = useSubscriptionValues({
		contentType,
		contentId,
	});
	const { accessStatus } = useSessionData();
	const skipSubscriptionForAnonUsers = accessStatus === AccessStatus.ANONYMOUS_ACCESS;

	const { error } = useSubscriptionInterop<
		useSuggestedEditsUpdatedSubscription$data,
		useSuggestedEditsUpdatedSubscription$variables
	>(
		graphql`
			subscription useSuggestedEditsUpdatedSubscription($id: ID!) {
				confluence_onSuggestedEditsUpdated(id: $id) {
					accountId
					__typename
				}
			}
		`,
		{
			variables: {
				id,
			},
			extensions,
			onSubscriptionComplete,
			onSubscriptionData(response) {
				if (response?.subscriptionData?.data?.confluence_onSuggestedEditsUpdated) {
					onSuggestedEditsUpdated();
				}
				onSubscriptionData(response);
			},
			skip: skip || !id || skipSubscriptionForAnonUsers,
			interopApolloAggClient: true,
		},
	);

	useEffect(() => {
		if (error) {
			const errorMessage =
				error?.message ||
				(error as any)?.errors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.originalError?.message ||
				error?.graphQLErrors?.[0]?.extensions?.data?.errors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.extensions?.message;

			const isIgnoredError =
				errorStringsToIgnore.some((errorString) => error?.message.includes(errorString)) ||
				!errorMessage;
			const statusCode =
				getStatusCode(error) || (error as any)?.errors?.[0]?.extensions?.statusCode;
			if (statusCode !== 403 && !isIgnoredError) {
				const e = new Error(
					`Subscription Error - useSuggestedEditsUpdatedSubscription:${errorMessage}`,
				);
				setContentErrorAttributes(e, {
					objectId: contentId,
					objectType: contentType,
				});
				getMonitoringClient().submitError(e, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
			}
			markErrorAsHandled(error);
		}
	}, [error, contentId, contentType]);
};
